import React from 'react';
import { useRouter } from 'next/router';
import { t } from '@lingui/macro';

import { PaginationButton } from './PaginationButton';

import s from './Pagination.module.scss';
import { useLingui } from '@lingui/react';

type Options = {
  pageCount: number;
  current?: number;
  showSize?: number;
  marginSize?: number;
};

const paginationItems = ({ pageCount, current = 1, showSize = 8, marginSize = 2 }: Options) => {
  if (pageCount <= showSize) {
    return Array(pageCount)
      .fill(undefined)
      .map((_, index) => index + 1);
  } else {
    const left = current > showSize - marginSize * 2 ? marginSize : showSize - marginSize - 1;
    const right =
      pageCount - current < showSize - marginSize * 2 ? showSize - marginSize - 1 : marginSize;
    const center = left > marginSize || right > marginSize ? 0 : showSize - 1 - marginSize * 2;

    const leftArray = Array(left)
      .fill(undefined)
      .map((_, index) => index + 1);
    const centerArray = Array(center)
      .fill(undefined)
      .map((_, index) => current + index - Math.floor((showSize - marginSize * 2 - 2) / 2));
    const rightArray = Array(right)
      .fill(undefined)
      .map((_, index) => pageCount - index)
      .reverse();

    return [...leftArray, ...(center > 0 ? ['...', ...centerArray] : []), '...', ...rightArray];
  }
};

type Props = {
  total: number;
  current: number;
  showSize?: number;
  marginSize?: number;
};
export const Pagination = ({ total, current, showSize, marginSize }: Props) => {
  const pages = paginationItems({ pageCount: total, current, showSize, marginSize });
  const router = useRouter();
  const { i18n } = useLingui();

  const { page, ...query } = router.query;
  const prevPage = Math.max(1, current - 1);
  const nextPage = Math.min(total, current + 1);

  return (
    <div className={s.pagination}>
      <div className={s.pagination__segment}>
        <div className={s.pagination__container}>
          <div className={s.pagination__layout}>
            <div className={s.pagination__control}>
              <PaginationButton
                href={{
                  pathname: router.pathname,
                  query: {
                    ...query,
                    ...(prevPage === 1 ? {} : { page: prevPage }),
                  },
                }}
                disabled={current <= 1}
                direction="left"
                aria-label={i18n._(t`Previous page`)}
              />
            </div>
            <div className={s.pagination__wrap}>
              <ul className={s.pagination__list}>
                {pages?.map((num, index) => (
                  <li className={s.pagination__item} key={index}>
                    <PaginationButton
                      href={
                        num !== '...'
                          ? {
                              pathname: router.pathname,
                              query: {
                                ...query,
                                ...(num === 1 ? {} : { page: num }),
                              },
                            }
                          : undefined
                      }
                      index={num}
                      current={current}
                      aria-label={`${t`Jump to page`} ${num}`}
                      tabIndex={num === current ? -1 : 0}
                    />
                  </li>
                ))}
              </ul>
            </div>
            <div className={s.pagination__control}>
              <PaginationButton
                href={{
                  pathname: router.pathname,
                  query: {
                    ...query,
                    ...(nextPage === total + 1 ? {} : { page: nextPage }),
                  },
                }}
                direction="right"
                disabled={current >= total}
                aria-label={i18n._(t`Next page`)}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
